import { useCallback, useEffect, useState } from 'react';
import {
  Loader2,
  AlertCircle,
  RefreshCw,
  X,
  MessageSquare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { DataState } from '../../context/DataProvider';
import { useChangeRequests } from '../../context/ChangeRequestProvider';
import { T, Btn } from '../project-register/shared.jsx';
import {
  getMeetingDetails,
  getUserConfiguredMeetingDetails,
} from '../../services/workflowApi';
import MeetingApprovalGate from './MeetingApprovalGate';

/**
 * The meeting detail page/panel, as a modal -- launched from wherever a
 * meeting is clicked (the Edit Meeting admin screen's eye icon, the
 * right-side workspace's Waiting tab, or the /meeting-summary/:eventId
 * route itself). Self-contained: owns its own fetch (meeting metadata +
 * llm_summary, one call) so every caller can just pass an eventId.
 */
const MeetingDetailModal = ({ eventId, isManual = false, onClose }) => {
  const { userGoogleId: accessToken } = DataState();
  const { stageCRChat } = useChangeRequests();
  const navigate = useNavigate();

  const [notification, setNotification] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!eventId || !accessToken) return;
    setStatus('loading');
    setError(null);
    try {
      const detail = isManual
        ? await getUserConfiguredMeetingDetails(accessToken, eventId)
        : await getMeetingDetails(accessToken, eventId);
      setNotification(detail);
      setStatus('idle');
    } catch (err) {
      setError(err?.message || 'Failed to load meeting details.');
      setStatus('failed');
    }
  }, [eventId, accessToken, isManual]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const subject = notification?.subject || 'Untitled Meeting';
  const processedData = notification?.llm_summary || null;
  const attendeesRaw = notification?.attendees || [];
  const attendees = attendeesRaw.map((a) => {
    const ea = a?.emailAddress || a;
    return ea?.name || ea?.address;
  });
  const startDt = notification?.start?.dateTime
    ? new Date(notification.start.dateTime)
    : null;
  const endDt = notification?.end?.dateTime
    ? new Date(notification.end.dateTime)
    : null;
  const durationMin =
    startDt && endDt ? Math.round((endDt - startDt) / 60000) : null;

  const handleBringToChat = () => {
    const jiraIssues =
      processedData?.task_wise_summary?.map((t) => ({
        key: t.issue_key,
        summary: t.jira_details?.summary,
        status: t.jira_details?.status,
        assignee: t.jira_details?.assignee_name,
        priority: t.jira_details?.priority,
        is_blocked: t.is_blocked_in_meeting,
      })) || [];

    stageCRChat({
      source_type: 'meeting',
      event_name: subject,
      app: isManual ? 'manual_meeting' : 'meeting',
      event_id: eventId,
      meeting_id: eventId,
      subject,
      organizer: notification?.organizer,
      attendees: attendeesRaw,
      start: notification?.start,
      end: notification?.end,
      description: notification?.bodyPreview,
      transcript_status: notification?.transcript_status,
      timestamp: notification?.timestamp,
      llm_summary: processedData,
      jira_issues: jiraIssues,
      action_items: processedData?.extracted_action_items || [],
      blockers: processedData?.unique_blockers || [],
    });
    window.dispatchEvent(new Event('oqlous:close-all-modals'));
    onClose?.();
    navigate('/chat');
  };

  return (
    // eslint-disable-next-line
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm md:p-6"
      onClick={onClose}
    >
      {/* eslint-disable-next-line */}
      <div
        // Same footprint as ProfileSettingsModal's default ('6xl') size --
        // this was capped at max-w-4xl with no height floor, so it sat much
        // smaller than every other full-screen modal in the app (Profile,
        // and anything else using that shared Modal at its default size).
        className="flex h-[88vh] max-h-[92vh] w-[94vw] max-w-6xl flex-col overflow-hidden rounded-xl"
        style={{ background: T.bg1, border: `1px solid ${T.br}` }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="flex shrink-0 items-center justify-between gap-3 px-5 py-4"
          style={{ borderBottom: `1px solid ${T.br}` }}
        >
          <div className="min-w-0">
            <h2
              className="truncate text-[15px] font-medium"
              style={{ color: T.text }}
            >
              {subject}
            </h2>
            {startDt && (
              <p className="mt-0.5 text-[11px]" style={{ color: T.muted }}>
                {startDt.toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
                {attendees.length > 0 && ` · ${attendees.length} attendees`}
                {durationMin ? ` · ${durationMin} min` : ''}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {status === 'idle' && (
              <Btn onClick={handleBringToChat}>
                <span className="flex items-center gap-1.5">
                  <MessageSquare size={13} />
                  Bring to chat
                </span>
              </Btn>
            )}
            {status === 'idle' && <Btn onClick={fetchData}>Refresh</Btn>}
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 transition-colors hover:opacity-70"
              style={{ color: T.muted }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-3 py-24">
              <Loader2
                className="h-7 w-7 animate-spin"
                style={{ color: T.info }}
              />
              <p className="text-[12px]" style={{ color: T.muted }}>
                Loading meeting…
              </p>
            </div>
          )}

          {status === 'failed' && (
            <div className="flex flex-col items-center gap-3 py-24 text-center">
              <AlertCircle className="h-8 w-8" style={{ color: T.danger }} />
              <p className="text-[13px]" style={{ color: T.textSec }}>
                {error}
              </p>
              <Btn onClick={fetchData}>
                <span className="flex items-center gap-1.5">
                  <RefreshCw size={13} />
                  Retry
                </span>
              </Btn>
            </div>
          )}

          {status === 'idle' && (
            <MeetingApprovalGate
              accessToken={accessToken}
              meetingId={eventId}
              llmSummary={processedData}
              isManual={isManual}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingDetailModal;
